/**
 * 10.9X — PERSONAGENS POR CLASSE. Decorator de SaveStorage: guarda VÁRIOS personagens (um por
 * classe) num único blob (compatível com a nuvem, que grava 1 blob por usuário). O SaveManager
 * enxerga só o personagem ATIVO — load/save operam sobre `chars[activeClass]`, então toda a
 * lógica existente (buildData/applyProgress/migrate) continua igual, sem saber de classes.
 *
 * Blob: { __mc: 2, activeClass, chars: { warrior: "<json>", archer: "<json>", ... } }
 * Save antigo (flat, sem __mc) = personagem único legado → IGNORADO (todos recomeçam do 0).
 */
import type { SaveStorage } from "./Storage";
import { GameConfig } from "../config/GameConfig";

interface Combined {
  __mc: 2;
  wipe?: number; // 10.9BV — token de reset global (GameConfig.save.wipeToken): diferente = TODOS os personagens zerados
  activeClass: string | null;
  chars: Record<string, string>; // classId -> JSON do save daquele personagem
}

export class CharacterStorage implements SaveStorage {
  constructor(private readonly inner: SaveStorage, private readonly baseKey: string) {}

  private read(): Combined {
    const raw = this.inner.load(this.baseKey);
    if (raw) {
      try {
        const o = JSON.parse(raw) as Partial<Combined>;
        // 10.9BV — RESET GLOBAL: token diferente do atual = apaga TODOS os personagens (e a nuvem
        // recebe o blob zerado no próximo push). Bumpar GameConfig.save.wipeToken zera os testadores.
        if (o && o.__mc === 2 && o.chars && typeof o.chars === "object" && (o.wipe ?? 0) === GameConfig.save.wipeToken) {
          return { __mc: 2, wipe: GameConfig.save.wipeToken, activeClass: typeof o.activeClass === "string" ? o.activeClass : null, chars: o.chars as Record<string, string> };
        }
      } catch {
        /* blob inválido/antigo → começa vazio */
      }
    }
    return { __mc: 2, wipe: GameConfig.save.wipeToken, activeClass: null, chars: {} };
  }
  private write(c: Combined): void {
    c.wipe = GameConfig.save.wipeToken;
    this.inner.save(this.baseKey, JSON.stringify(c));
  }

  // ---- SaveStorage: o SaveManager usa; `key` é ignorada, opera no personagem ATIVO ----
  load(_key: string): string | null {
    const c = this.read();
    if (!c.activeClass) return null;
    return c.chars[c.activeClass] ?? null;
  }
  save(_key: string, value: string): void {
    const c = this.read();
    if (!c.activeClass) return; // sem personagem ativo, não grava (evita lixo)
    c.chars[c.activeClass] = value;
    this.write(c);
  }
  remove(_key: string): void {
    const c = this.read();
    if (!c.activeClass) return;
    delete c.chars[c.activeClass];
    this.write(c);
  }

  // ---- API de personagens (o Game usa) ----
  activeClassId(): string | null {
    return this.read().activeClass;
  }
  /** Define qual personagem está ativo (o próximo load/save opera nele). */
  setActive(classId: string): void {
    const c = this.read();
    c.activeClass = classId;
    this.write(c);
  }
  hasCharacter(classId: string): boolean {
    return !!this.read().chars[classId];
  }
  listClasses(): string[] {
    return Object.keys(this.read().chars);
  }
  /** Resumo pra tela: classe + nível + NOME (parseia o blob de cada personagem). */
  summaries(): { classId: string; level: number; name: string }[] {
    const c = this.read();
    return Object.keys(c.chars).map((k) => {
      let level = 1;
      let name = "";
      try {
        const s = JSON.parse(c.chars[k]) as { level?: unknown; progress?: { charName?: unknown } };
        if (typeof s.level === "number") level = s.level;
        if (typeof s.progress?.charName === "string") name = s.progress.charName;
      } catch { /* */ }
      return { classId: k, level, name };
    });
  }
  deleteCharacter(classId: string): void {
    const c = this.read();
    delete c.chars[classId];
    if (c.activeClass === classId) c.activeClass = null;
    this.write(c);
  }
}
